import { isNormalPositive } from '@bifrost-platform/bifront-sdk-react-biholder';
import {
  BN,
  compareLowerStr,
  useWallet,
} from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import AssetValueRow from '@/components/DashBoard/AssetValueRow';
import Button from '@/components/atom/Button';
import { FlexBox } from '@/components/atom/FlexBox';
import { GlassPanel } from '@/components/atom/GlassPanel';
import { StyledText } from '@/components/atom/Text';
import useBtcfi from '@/hooks/api/useBtcfi';
import useInitializer from '@/hooks/bifrontSdk/useInitializer';
import useEnv from '@/hooks/useEnv';
import { dialogActionAtom } from '@/store/uiStore';
import { dashboardCardVariant } from '@/styles/motion';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../atom/Tooltip';

const Container = styled(GlassPanel)`
  width: 400px;
`;

const ButtonPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.27027027fr;
  ${(p) => p.theme.gap.md}
`;

interface Props {
  isMobile: boolean;
}

export default function PledgedAssetPanel({ isMobile }: Props) {
  // translate
  const { t } = useTranslation('dashboard');

  // initializer
  const { syncedEnv, syncedChains, syncedPairs, syncedBtcfi } =
    useInitializer();

  // env
  const { tokensBifrostBtc } = useEnv();

  // wallet
  const { account } = useWallet();

  // btcfi
  const {
    assetSets,
    depositAmountValue,
    borrowAmountValue,
    maxBorrowAmountValue,
    borrowableAmountValue,
    isLoading: isLoadingBtcfi,
  } = useBtcfi();

  // store
  const [, setDialogAction] = useAtom(dialogActionAtom);

  // memo
  const assets = useMemo(
    () =>
      tokensBifrostBtc.map((token) => ({
        ...assetSets.find(({ symbol }) =>
          compareLowerStr(symbol, token.symbol)
        ),
      })),
    [assetSets, tokensBifrostBtc]
  );
  const isFirstTime = useMemo(
    () =>
      account &&
      new BN(depositAmountValue).lte(0) &&
      new BN(borrowableAmountValue).lte(0) &&
      new BN(borrowAmountValue).lte(0),
    [account, depositAmountValue, borrowableAmountValue, borrowAmountValue]
  );
  const isWithdrawable = useMemo(
    () =>
      isNormalPositive(
        assets.reduce(
          (prev, curr) => new BN(prev).plus(curr.maxWithdrawAmount ?? 0),
          new BN(0)
        ),
        true
      ),
    [assets]
  );
  const isBorrowed = useMemo(
    () => isNormalPositive(borrowAmountValue, true),
    [borrowAmountValue]
  );
  const isBorrowable = useMemo(
    () => isNormalPositive(maxBorrowAmountValue, true),
    [maxBorrowAmountValue]
  );
  const isBorrowedAll = useMemo(
    () => isBorrowed && !isBorrowable,
    [isBorrowed, isBorrowable]
  );
  const isDisabledWithdraw = useMemo(
    () => !(account && isWithdrawable),
    [account, isWithdrawable]
  );
  const isDisabledDeposit = useMemo(
    () => !(account && !isFirstTime),
    [account, isFirstTime]
  );
  const synced = useMemo(
    () => syncedEnv && syncedChains && syncedPairs && syncedBtcfi,
    [syncedBtcfi, syncedChains, syncedEnv, syncedPairs]
  );
  const isLoading = useMemo(
    () => !!(account && !synced && isLoadingBtcfi),
    [account, isLoadingBtcfi, synced]
  );

  // handler
  const handleWithdrawClick = useCallback(
    () =>
      setDialogAction({ isShow: true, type: isMobile ? 'mobile' : 'withdraw' }),
    [isMobile, setDialogAction]
  );
  const handleDepositClick = useCallback(
    () =>
      setDialogAction({ isShow: true, type: isMobile ? 'mobile' : 'deposit' }),
    [isMobile, setDialogAction]
  );

  return (
    <Container
      as={motion.div}
      variants={dashboardCardVariant}
      $flex
      $direction="column"
      $gap="lg"
      $padding="xl"
      $justify="space-between"
      $rounded="xl"
    >
      <FlexBox $flex $direction="column" $gap="xl">
        <StyledText as="h3" $fontWeight="medium">
          {t('pledgedAsset')}
        </StyledText>
        <FlexBox as="ul" $flex $direction="column" $gap="md">
          {assets.map((asset, assetIndex) => (
            <AssetValueRow
              value={asset.depositAmount}
              token={asset.token}
              price={asset.price}
              isLoading={isLoading}
              key={assetIndex}
            />
          ))}
        </FlexBox>
      </FlexBox>
      <ButtonPanel>
        {isBorrowedAll ? (
          <Tooltip placement="top" offset={16}>
            <TooltipTrigger asChild>
              <Button
                disabled
                $padding={{
                  x: 'xl',
                  y: 'md',
                }}
                $rounded="lg"
                $variant="secondary"
                $fontWeight="bold"
                onClick={handleWithdrawClick}
              >
                {t('withdraw')}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <TooltipArrow />
              <StyledText $fontSize="md" $fontWeight="bold">
                {t('withdrawAfterRepaying')}
              </StyledText>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            $padding={{
              x: 'xl',
              y: 'md',
            }}
            $rounded="lg"
            $variant="secondary"
            $fontWeight="bold"
            disabled={isDisabledWithdraw}
            onClick={handleWithdrawClick}
          >
            {t('withdraw')}
          </Button>
        )}
        <Button
          $padding={{
            x: 'xl',
            y: 'md',
          }}
          $rounded="lg"
          $variant="primary"
          $fontWeight="bold"
          disabled={isDisabledDeposit}
          onClick={handleDepositClick}
        >
          {t('provide')}
        </Button>
      </ButtonPanel>
    </Container>
  );
}
